/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-const-assign */
/* eslint-disable no-unreachable */
import React, { useCallback, useLayoutEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  LineElement,
  Legend,
  BarElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import CustomSelect from "~components/Form/components/Select";
import useHeader from "~components/Header/hooks/useHeader";
import GraficoClienteLegenda from "./GraficoClienteLegenda";
import RotaDoDiaFiltroGraficoCliente from "../Filtros/RotaDoDiaFiltroGraficoCliente";
import BarChart from "./BarChart";
import LineChart from "./LineChart";
import useSelect from "~components/Form/hooks/useSelect";
import PropTypes from "prop-types";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartDataLabels,
  Legend,
  Tooltip
);

const GraficoCliente = ({
  faturamentos,
  faturamentoLastCreatedAtDate,
  filtros,
  govs,
  filters,
  cleanFilters,
  setFilters,
  startDate,
  setStartDate,
  setSelects,
  selects,
}) => {
  const { path } = useHeader();
  const regex = /^\/roteirizacao\/pesquisa-de-preco\/\d+$/;
  const isValidRoute = regex.test(path);
  const faturamento = useSelect("faturamento");
  const volume = useSelect("volume");

  useLayoutEffect(() => {
    faturamento.upDateDefaultValue({ label: "Faturamento (R$)", value: "revenues" });
    volume.upDateDefaultValue({ label: "Volume (t)", value: "volumes" });
    faturamento.upDateValue("revenues");
    volume.upDateValue("volumes");
  }, []);

  function onChangeFaturamento(e) {
    faturamento.upDateValue(e.value);
    faturamento.upDateDefaultValue(e || "");
  }

  function onChangeVolume(e) {
    volume.upDateValue(e.value);
    volume.upDateDefaultValue(e || "");
  }

  const datas = useMemo(() => {
    const formatMonth = (date) => {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${month}/${year}`;
    };
    const getMonthsList = () => {
      const today = new Date();
      const months = [];
      const lastYearSameMonth = new Date(today.getFullYear() - 1, today.getMonth());
      months.push(formatMonth(lastYearSameMonth));

      for (let i = 4; i > 0; i--) {
        const prevMonth = new Date(today.getFullYear(), today.getMonth() - i);
        months.push(formatMonth(prevMonth));
      }

      months.push(formatMonth(today));

      return months;
    };

    return getMonthsList();
  }, []);

  const clientes = useMemo(() => {
    const map = new Map();
    for (const obj of faturamentos || []) {
      map.set(obj.clienteId, {
        id: obj.clienteId,
        codigo: obj.codigoCliente,
        nome: obj.nomeCliente,
      });
    }

    return Array.from(map.values());
  }, [faturamentos]);

  const grupos = useMemo(() => {
    const map = new Map();

    for (const obj of faturamentos || []) {
      map.set(obj.grupoMaterialId, {
        id: obj.grupoMaterialId,
        nome: obj.nomeGrupoMaterial,
      });
    }

    return Array.from(map.values());
  }, [faturamentos]);

  const produtos = useMemo(() => {
    const map = new Map();
    for (const obj of faturamentos || []) {
      map.set(obj.produtoId, {
        id: obj.produtoId,
        grupoMaterialId: obj.grupoMaterialId,
        codigo: obj.codigoProduto,
        nome: obj.nomeProduto,
      });
    }

    return Array.from(map.values());
  }, [faturamentos]);

  const newFaturamento = useMemo(
    () =>
      (faturamentos || []).map((faturamento) => ({
        ...faturamento,
        newData: new Date(faturamento.data).toLocaleString("pt-BR", { year: "2-digit", month: "2-digit" }),
      })),
    [faturamentos]
  );

  const sumIndicador = useCallback((faturamentos, indicador, fob = false) => {
    const sumValues = (items, key) =>
      items.reduce((soma, item) => {
        const valor = item[key];
        const valorNumerico = typeof valor === "string" ? Number.parseFloat(valor.replace(",", ".")) : valor;
        return soma + (Number.isNaN(valorNumerico) ? 0 : valorNumerico);
      }, 0);
    const sumFaturamento = sumValues(faturamentos, indicador);
    if (!fob) return sumFaturamento;
    const sumVolume = sumValues(faturamentos, "volume");
    return sumVolume == 0 ? 0 : sumFaturamento / sumVolume;
  }, []);

  const processFilters = (item) => {
    const isProduct = filters.grupoMaterialId;
    return {
      id: isProduct ? item.grupoMaterialId : item.id,
      name: item.nome,
    };
  };

  const processMonthData = (item, data) => {
    const isProduct = filters.grupoMaterialId;
    const faturamentos = newFaturamento.filter(
      (f) => (isProduct ? f.produtoId : f.grupoMaterialId) === item.id && f.newData === data
    );
    return {
      mes: data,
      volume: sumIndicador(faturamentos, "volume"),
      volumeDtu: sumIndicador(faturamentos, "volumeDu"),
      faturamentoBruto: sumIndicador(faturamentos, "faturamentoBruto"),
      precoFobLiquido: sumIndicador(faturamentos, "precoFobLiquidoTotal", true),
    };
  };

  const createClienteItemData = (cliente, item) => {
    const processedItem = processFilters(item);
    return {
      cliente: cliente.razaoSocial,
      label: processedItem.name,
      id: processedItem.id,
      mes: (datas || []).map((data) => processMonthData(item, data)),
    };
  };

  const createClienteData = (cliente) => {
    const items = filters.grupoMaterialId ? produtos : grupos;
    return items.map((item) => createClienteItemData(cliente, item));
  };

  const dados = useMemo(() => {
    return clientes.flatMap(createClienteData);
  }, [clientes, produtos, grupos, newFaturamento, datas, filters.grupoMaterialId, govs]);

  const getData = (cliente, column) =>
    (datas || []).map((label) => {
      const mesData = cliente.mes.find((d) => d.mes === label);
      return mesData ? mesData[column] : 0;
    });

  const getClienteColors = (cliente) => {
    return (datas || []).map((label) => {
      const mesData = cliente.mes.find((d) => d.mes === label);
      return mesData?.cor || "#034EA2";
    });
  };

  const createVolumeGroups = () => {
    return dados.map((cliente) => ({
      name: cliente.label,
      id: cliente.id,
      volumes: getData(cliente, "volume"),
      volumesDtus: getData(cliente, "volumeDtu"),
      colors: getClienteColors(cliente),
      filtro: filters.grupoMaterialId ? "produto" : "grupo",
    }));
  };

  const createVolumeGovs = () => {
    return govs.map((gov) => ({
      name: gov.nomeGrupoMaterial,
      id: gov.grupoMaterialId,
      volumes: gov.metaGov ?? 0,
      volumesDtus: gov.metaGov ?? 0,
    }));
  };

  const dataVolume = useMemo(() => {
    const groups = createVolumeGroups();
    const dadosGov = createVolumeGovs();
    return { labels: datas || [], groups, govs: dadosGov };
  }, [dados, datas, govs]);

  const createFaturamentoGroups = () => {
    return dados.map((cliente) => ({
      name: cliente.label,
      id: cliente.id,
      revenues: getData(cliente, "faturamentoBruto"),
      liquidos: getData(cliente, "precoFobLiquido"),
      color: "#888888",
      filtro: filters.grupoMaterialId ? "produto" : "grupo",
    }));
  };

  const createFaturamentoGovs = () => {
    return govs.map((gov) => ({
      name: gov.nomeGrupoMaterial,
      id: gov.grupoMaterialId,
      revenues: gov?.metaFob ?? 0,
      liquidos: gov?.metaFob ?? 0,
    }));
  };

  const dataFaturamento = useMemo(() => {
    const groups = createFaturamentoGroups();
    const dadosGov = createFaturamentoGovs();
    return { labels: datas || [], groups, govs: dadosGov };
  }, [dados, datas, govs]);

  const getMaxPositiveCount = (produtos, opcao) => {
    console.log(produtos, opcao, 'produtos dentro do max')
    return Math.max(
      ...produtos.map((produto) => {
        if (Array.isArray(produto[opcao])) {
          return [...produto[opcao]].slice(1).filter(num => num > 0).length;
        }

        return 0;
      })
    );
  };

  const calculaSoma = (array, opcao) => {
    return array.reduce((acumulador, valor) => {
      return (
        acumulador + (valor[opcao]?.reduce ? valor[opcao].slice(1).reduce((sum, v) => sum + v, 0) : valor[opcao] || 0)
      );
    }, 0);
  };

  const calculaTotal = (dataGroups, newValue, datas) => {
    console.log(newValue, 'newValue dentro do total')
    if (dataGroups.length === 0) return 0;
    const soma = calculaSoma(dataGroups, newValue);
    const maiorQuantidade = getMaxPositiveCount(dataGroups, newValue);
    return soma / maiorQuantidade;
  };

  const totalFaturamento = useMemo(
    () => calculaTotal(dataFaturamento.groups, faturamento.initValue, datas),
    [dataFaturamento.groups, faturamento.initValue, datas]
  );

  const totalVolume = useMemo(
    () => calculaTotal(dataVolume.groups, volume.initValue, datas),
    [dataVolume.groups, volume.initValue, datas]
  );

  return (
    <div className="containerForm container bg-white p-7 rounded-lg mt-[28px] relative">
      <RotaDoDiaFiltroGraficoCliente
        filtros={filtros}
        filters={filters}
        setFilters={setFilters}
        cleanFilters={cleanFilters}
        setSelects={setSelects}
        selects={selects}
      />
      <div className=" container-grafico">
        <div>
          <h4 className="text-lg text-primaria font-bold h-[50px] max-w-[400px] mx-auto">
            {isValidRoute ? (
              "Client Share"
            ) : (
              <CustomSelect
                placeholder="Volume"
                name="volume"
                className="select-filter focus:outline-none focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:border-none focus-visible:shadow-none"
                options={[
                  { label: "Volume (t)", value: "volumes" },
                  { label: "Volume DU (t)", value: "volumesDtus" },
                ]}
                centerBlueText
                onChange={(e) => {
                  onChangeVolume(e);
                }}
                {...volume}
              />
            )}
          </h4>
          <div className="mb-8 border-r shrink grow basis-1/2 md:p-8 md:mb-0">
            <BarChart
              data={dataVolume}
              escolha={volume.initValue}
              filters={filters}
              items={dataVolume.groups}
              title="Volume Médio"
              tipo="barra"
              total={totalVolume}
            />
          </div>
        </div>
        <div>
          <div className="max-w-[400px] mx-auto">
            {isValidRoute ? (
              <h4 className="text-lg text-primaria font-bold text-center h-[50px]">Sell Out</h4>
            ) : (
              <CustomSelect
                placeholder="Faturamento (R$)"
                name="faturamento"
                className="select-filter focus:outline-none focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:border-none focus-visible:shadow-none"
                options={[
                  { label: "Faturamento (R$)", value: "revenues" },
                  { label: "Fob. Líquido (R$/t)", value: "liquidos" },
                ]}
                centerBlueText
                onChange={(e) => {
                  onChangeFaturamento(e);
                }}
                {...faturamento}
              />
            )}
          </div>
          <div className="shrink grow basis-1/2 md:p-8 relative top-[-1.1rem]">
            <LineChart data={dataFaturamento} escolha={faturamento.initValue} />
            <GraficoClienteLegenda
              items={dataFaturamento.groups}
              filters={filters}
              title="Faturamento Médio:"
              tipo="linha"
              escolha={faturamento.initValue}
              total={totalFaturamento}
            />
          </div>
        </div>
      </div>
      {faturamentoLastCreatedAtDate ? (
        <p className="flex justify-center mt-2">
          Atualizado em: <strong className="ml-2">{faturamentoLastCreatedAtDate}</strong>
        </p>
      ) : null}
    </div>
  );
};

GraficoCliente.propTypes = {
  faturamentos: PropTypes.array,
  faturamentoLastCreatedAtDate: PropTypes.string,
  filtros: PropTypes.array,
  govs: PropTypes.array,
  filters: PropTypes.object,
  cleanFilters: PropTypes.func,
  setFilters: PropTypes.func,
  startDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func,
  setSelects: PropTypes.func,
  selects: PropTypes.object,
};

export default GraficoCliente;
